import React from "react";
import LinkifyIt from "linkify-it";

/**
 * An inert text segment.
 */
interface TextToken {
  /** Discriminator identifying a text segment. */
  type: "text";
  /** The literal text to render. */
  value: string;
}

/**
 * A safe http(s) link segment.
 */
interface LinkSegment {
  /** Discriminator identifying a link segment. */
  type: "link";
  /** Validated http(s) link address. */
  href: string;
  /** Visible text for the link. */
  text: string;
}

/**
 * A single piece of decorated content: either inert text or a safe link.
 */
type LinkToken = TextToken | LinkSegment;

/**
 * Decorators for React components.
 */
export class ComponentDecorators {
  private static readonly markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

  /**
   * Only http(s) URLs are ever rendered as clickable links. Everything else
   * (javascript:, data:, mailto:, file:, schemeless, ...) is treated as plain
   * text. This is a deliberate, security-first allow-list.
   */
  private static readonly safeSchemePattern = /^https?:\/\//i;

  /**
   * Shared linkify-it instance. Fuzzy matching is disabled so that only
   * explicit http(s) URLs are detected (no bare "www.", emails or IPs).
   */
  private static readonly linkify = new LinkifyIt({}, { fuzzyLink: false, fuzzyEmail: false, fuzzyIP: false });

  /**
   * Decorator for links that should open in the new tab.
   *
   * @param href Address of the link
   * @param text Visible text of the link
   * @param key Key of the link
   * @returns React component with target and rel
   */
  public static newTabLinkDecorator(href: string, text: string, key: number): JSX.Element {
    return (
      <a href={href} key={key} target="_blank" rel="noreferrer noopener" style={{ color: "var(--rca-link-color, #0078d4)" }}>
        {text}
      </a>
    );
  }

  /**
   * Replace text links in provided object with clickable links
   *
   * @param obj Object to be processed
   * @returns React node with links replaced by clickable links
   */
  public static replaceLinks(obj: JSX.Element | string): JSX.Element {
    if (typeof obj !== "string") {
      return <>{ComponentDecorators.decorateNode(obj)}</>;
    }

    return <>{ComponentDecorators.renderTokens(ComponentDecorators.tokenize(obj))}</>;
  }

  /**
   * Tokenize a string into ordered text/link segments.
   *
   * Markdown-style links (`[text](url)`) take precedence; the remaining gaps are
   * scanned by linkify-it for plain http(s) URLs. Every candidate href must pass
   * the http(s) scheme allow-list or it is emitted as inert text.
   *
   * @param content The string content to tokenize.
   * @returns Ordered list of text and link tokens.
   */
  public static tokenize(content: string): LinkToken[] {
    const tokens: LinkToken[] = [];
    let currentIndex = 0;
    let match: RegExpExecArray | null;

    ComponentDecorators.markdownLinkPattern.lastIndex = 0;

    while ((match = ComponentDecorators.markdownLinkPattern.exec(content)) !== null) {
      const matchStart = match.index;

      if (matchStart > currentIndex) {
        ComponentDecorators.appendPlainTextTokens(content.substring(currentIndex, matchStart), tokens);
      }

      ComponentDecorators.appendLink(match[2], match[1], tokens);
      currentIndex = matchStart + match[0].length;
    }

    if (currentIndex < content.length) {
      ComponentDecorators.appendPlainTextTokens(content.substring(currentIndex), tokens);
    }

    return tokens;
  }

  /**
   * Scan a markdown-free string for plain http(s) URLs and append the resulting
   * text/link tokens.
   *
   * @param text The string to scan.
   * @param tokens The token list to append to.
   */
  private static appendPlainTextTokens(text: string, tokens: LinkToken[]): void {
    if (text.length === 0) {
      return;
    }

    const matches = ComponentDecorators.linkify.match(text);

    if (!matches || matches.length === 0) {
      ComponentDecorators.appendText(text, tokens);
      return;
    }

    let lastIndex = 0;

    for (const linkMatch of matches) {
      if (linkMatch.index > lastIndex) {
        ComponentDecorators.appendText(text.substring(lastIndex, linkMatch.index), tokens);
      }

      ComponentDecorators.appendLink(linkMatch.url, linkMatch.text, tokens);
      lastIndex = linkMatch.lastIndex;
    }

    if (lastIndex < text.length) {
      ComponentDecorators.appendText(text.substring(lastIndex), tokens);
    }
  }

  /**
   * Append a link token if the href passes the http(s) allow-list; otherwise the
   * original text is appended as inert text.
   *
   * @param href Candidate link address.
   * @param text Visible text for the link.
   * @param tokens The token list to append to.
   */
  private static appendLink(href: string, text: string, tokens: LinkToken[]): void {
    if (ComponentDecorators.safeSchemePattern.test(href)) {
      tokens.push({ type: "link", href, text });
    } else {
      ComponentDecorators.appendText(text, tokens);
    }
  }

  /**
   * Append a text token, coalescing with a preceding text token when possible.
   *
   * @param value The text to append.
   * @param tokens The token list to append to.
   */
  private static appendText(value: string, tokens: LinkToken[]): void {
    if (value.length === 0) {
      return;
    }

    const last = tokens[tokens.length - 1];

    if (last && last.type === "text") {
      last.value += value;
    } else {
      tokens.push({ type: "text", value });
    }
  }

  /**
   * Render tokens into React nodes.
   *
   * @param tokens The tokens to render.
   * @returns React nodes for the tokens.
   */
  private static renderTokens(tokens: LinkToken[]): React.ReactNode[] {
    return tokens.map((token, index) =>
      token.type === "link"
        ? ComponentDecorators.newTabLinkDecorator(token.href, token.text, index)
        : ((<React.Fragment key={index}>{token.value}</React.Fragment>) as React.ReactNode)
    );
  }

  /**
   * Recursively decorate a React node, tokenizing string children only and
   * preserving the surrounding element structure.
   *
   * @param node The node to decorate.
   * @returns The decorated node.
   */
  private static decorateNode(node: React.ReactNode): React.ReactNode {
    if (typeof node === "string") {
      return ComponentDecorators.renderTokens(ComponentDecorators.tokenize(node));
    }

    if (Array.isArray(node)) {
      return (node as React.ReactNode[]).map((child, index) => (
        <React.Fragment key={index}>{ComponentDecorators.decorateNode(child)}</React.Fragment>
      ));
    }

    if (React.isValidElement(node)) {
      const element = node as React.ReactElement<React.PropsWithChildren<unknown>>;

      // Never re-decorate an existing anchor: tokenizing its children could
      // produce invalid nested <a> tags and shadow the original link's href.
      if (typeof element.type === "string" && element.type.toLowerCase() === "a") {
        return element;
      }

      if (element.props.children === undefined) {
        return element;
      }

      return React.cloneElement(element, undefined, ComponentDecorators.decorateNode(element.props.children));
    }

    return node;
  }
}
